import React from 'react';

const OrderActions = ({
  orderId,
  orderStatus,
  cart,
  savingOrder,
  submittingOrder,
  markingAllOutOfStock,
  hasChargeError,
  isModal,
  onMarkAllOutOfStock,
  onShowNoteModal,
  onShowCancelModal,
  onCancel,
  onSave,
  onSubmit,
  // Optional: the Generate Payment Link button renders only when a handler is passed.
  onGeneratePaymentLink,
  generatingPaymentLink = false,
  isOrderLocked = false,
  // Paid through any channel (in-app, payment link, credit line) — a link would invite a second payment.
  isOrderPaid = false
}) => {
  const canMarkOutOfStock = orderId && 
    orderStatus && 
    !['cancelled', 'delivered', 'ordered', 'out_of_stock'].includes(orderStatus);
  
  const hasNonOutOfStockItems = cart.filter(item => !item.outOfStock).length > 0;

  // Check if Save button should be disabled when status is "initiated"
  const isStatusInitiated = orderStatus && String(orderStatus).toLowerCase().trim() === 'initiated';
  const shouldDisableSave = savingOrder || !orderId || cart.length === 0 || isStatusInitiated || hasChargeError;
  const shouldDisableSubmit = submittingOrder || !orderId || cart.length === 0 || !hasNonOutOfStockItems || hasChargeError;
  const chargeErrorReason = 'Negative values are not allowed';
  const canCancelOrder = Boolean(orderId) && String(orderStatus || '').toLowerCase() !== 'cancelled';
  const shouldDisableCancel = cart.length === 0;

  // One grey button used to mean four different things, and ops could not tell them apart —
  // a prefilled-cart fetch that quietly failed (leaving orderId null) looks identical to a
  // deliberate status block. Name the reason so "Save is broken" tickets carry a cause.
  const saveDisabledReason = savingOrder
    ? 'Saving…'
    : !orderId
      ? 'No order loaded yet — reopen this cart if it does not appear'
      : cart.length === 0
        ? 'Add at least one medicine to the cart'
        : isStatusInitiated
          ? 'This order has already been submitted for payment. Edit the cart or address and '
            + 'press Create Transaction to apply the change — saving it back would remove the '
            + "subscriber's pending payment request from their app."
          : hasChargeError
            ? chargeErrorReason
            : '';

  // Generating a link runs the same submit chain as Create Transaction, so it needs the same
  // preconditions, and the two must never run at the same time.
  const submitBlocked = submittingOrder || generatingPaymentLink || !orderId || cart.length === 0
    || !hasNonOutOfStockItems;
  const shouldDisablePaymentLink = submitBlocked || savingOrder || isOrderLocked || isOrderPaid;
  const paymentLinkDisabledReason = isOrderPaid
    ? 'Payment already received for this order'
    : isOrderLocked
    ? 'This order is already paid or locked'
    : !hasNonOutOfStockItems
      ? 'Add at least one in-stock medicine to the cart'
      : '';

  return (
    <div className="flex justify-between items-center pt-4 border-t border-gray-200">
      <button
        onClick={onMarkAllOutOfStock}
        disabled={markingAllOutOfStock || cart.length === 0 || !canMarkOutOfStock}
        className={`px-6 py-2.5 border border-orange-600 rounded-md font-medium transition-colors ${
          markingAllOutOfStock || cart.length === 0 || !canMarkOutOfStock
            ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed'
            : 'text-orange-600 hover:bg-orange-50'
        }`}
      >
        {markingAllOutOfStock ? 'Marking...' : 'Mark All as Out of Stock'}
      </button>
      <div className="flex space-x-3">
        {orderId && (
          <button
            onClick={onShowNoteModal}
            className="px-6 py-2.5 border border-purple-600 rounded-md text-purple-600 hover:bg-purple-50 transition-colors font-medium"
          >
            Note
          </button>
        )}
        <button
          onClick={canCancelOrder && onShowCancelModal ? onShowCancelModal : onCancel}
          disabled={shouldDisableCancel}
          title={shouldDisableCancel ? 'Add at least one medicine to the cart' : undefined}
          className={`px-6 py-2.5 border border-red-600 rounded-md font-medium transition-colors ${
            shouldDisableCancel
              ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed'
              : 'text-red-600 hover:bg-red-50'
          }`}
        >
          Cancel
        </button>
        <button
          onClick={onSave}
          disabled={shouldDisableSave}
          title={shouldDisableSave ? saveDisabledReason : undefined}
          className={`px-6 py-2.5 border border-green-600 rounded-md font-medium transition-colors ${
            shouldDisableSave
              ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed'
              : 'text-green-600 hover:bg-green-50'
          }`}
        >
          {savingOrder ? 'Saving...' : 'Save'}
        </button>
        {onGeneratePaymentLink && (
          <button
            onClick={onGeneratePaymentLink}
            disabled={shouldDisablePaymentLink}
            title={shouldDisablePaymentLink ? paymentLinkDisabledReason || undefined : 'Sends a Razorpay link for the amount after cashback'}
            className={`px-6 py-2.5 border border-teal-600 rounded-md font-medium transition-colors ${
              shouldDisablePaymentLink
                ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed'
                : 'text-teal-700 hover:bg-teal-50'
            }`}
          >
            {generatingPaymentLink ? 'Generating...' : 'Generate Payment Link'}
          </button>
        )}
        <button
          onClick={onSubmit}
          disabled={shouldDisableSubmit}
          title={hasChargeError ? chargeErrorReason : undefined}
          className={`px-6 py-2.5 rounded-md font-medium text-white transition-colors ${
            shouldDisableSubmit
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          {submittingOrder ? 'Submitting...' : 'Create Transaction'}
        </button>
      </div>
    </div>
  );
};

export default OrderActions;

